import { useEffect, useState } from 'react';
import { Calendar, Clock, Euro, Store, Tag, User, Wallet, X } from 'lucide-react';
import CustomSelect from './CustomSelect';
import { usePopup } from '../contexts/PopupContext';
import { ChevronLeft, ChevronRight } from 'lucide-react';
import { extractMoneyDigits, formatMoneyInput, moneyDigitsToNumber } from '../utils/moneyInput';

const createInitialState = () => ({
  amount: { enabled: false, value: '' },
  category: { enabled: false, value: '' },
  destination: { enabled: false, mode: 'wallet', walletId: '', personName: '' },
  store: { enabled: false, value: '' },
  date: { enabled: false, value: '' },
  time: { enabled: false, value: '' }
});

function BulkEditDialog({
  isOpen,
  onClose,
  onApply,
  selectedCount = 0,
  categories = [],
  wallets = [],
  existingPersons = []
}) {
  const { showError } = usePopup();
  const [formState, setFormState] = useState(createInitialState);

  useEffect(() => {
    if (!isOpen) {
      return;
    }
    setFormState(createInitialState());
  }, [isOpen]);

  if (!isOpen) {
    return null;
  }

  const handleToggleField = (field) => {
    setFormState(prev => ({
      ...prev,
      [field]: {
        ...prev[field],
        enabled: !prev[field].enabled
      }
    }));
  };

  const handleDateShift = (days) => {
    setFormState(prev => {
      if (!prev.date.enabled || !prev.date.value) return prev;
      const [year, month, day] = prev.date.value.split('-').map(Number);
      const date = new Date(year, month - 1, day);
      date.setDate(date.getDate() + days);
      
      const newYear = date.getFullYear();
      const newMonth = String(date.getMonth() + 1).padStart(2, '0');
      const newDay = String(date.getDate()).padStart(2, '0');
      
      return {
        ...prev,
        date: { ...prev.date, value: `${newYear}-${newMonth}-${newDay}` }
      };
    });
  };

  const handleAmountChange = (event) => {
    const digits = extractMoneyDigits(event.target.value);
    setFormState(prev => ({
      ...prev,
      amount: { ...prev.amount, value: digits }
    }));
  };

  const handleSubmit = (event) => {
    event.preventDefault();

    const anyEnabled = Object.values(formState).some(field => field.enabled);
    if (!anyEnabled) {
      showError('Seleziona almeno un campo da modificare.');
      return;
    }

    if (formState.amount.enabled && !formState.amount.value) {
      showError('Inserisci un importo valido.');
      return;
    }

    if (formState.category.enabled && !formState.category.value) {
      showError('Seleziona una categoria valida.');
      return;
    }

    if (formState.destination.enabled) {
      if (formState.destination.mode === 'wallet' && !formState.destination.walletId) {
        showError('Seleziona un conto valido.');
        return;
      }
      if (formState.destination.mode === 'debt' && !formState.destination.personName.trim()) {
        showError('Inserisci una persona valida.');
        return;
      }
    }

    if (formState.store.enabled && !formState.store.value.trim()) {
      showError('Inserisci un negozio valido.');
      return;
    }

    if (formState.date.enabled && !formState.date.value) {
      showError('Seleziona una data valida.');
      return;
    }

    if (formState.time.enabled && !formState.time.value) {
      showError('Seleziona un orario valido.');
      return;
    }

    onApply?.({
      amountEnabled: formState.amount.enabled,
      amountValue: formState.amount.enabled ? moneyDigitsToNumber(formState.amount.value) : null,
      categoryEnabled: formState.category.enabled,
      categoryValue: formState.category.value,
      destinationEnabled: formState.destination.enabled,
      destinationType: formState.destination.mode,
      walletId: formState.destination.walletId,
      personName: formState.destination.personName.trim(),
      storeEnabled: formState.store.enabled,
      storeValue: formState.store.value.trim(),
      dateEnabled: formState.date.enabled,
      dateValue: formState.date.value,
      timeEnabled: formState.time.enabled,
      timeValue: formState.time.value
    });
  };

  const categoryOptions = categories.map(category => ({
    value: category.name,
    label: category.name,
    icon: category.icon
  }));

  const walletOptions = wallets.map(wallet => ({
    value: wallet.id,
    label: wallet.name,
    icon: (
      <div
        className="w-4 h-4 rounded-full border-2 border-white shadow-sm"
        style={{ backgroundColor: wallet.color === 'rainbow' ? '#6366f1' : wallet.color }}
      />
    )
  }));

  const destinationDisabled = !formState.destination.enabled;

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal-content" onClick={event => event.stopPropagation()}>
        <div className="bg-blue-600/90 backdrop-blur-sm text-white p-4 rounded-t-3xl">
          <div className="flex items-center justify-between">
            <button
              onClick={onClose}
              className="text-white/80 hover:text-white transition-colors"
            >
              <X className="w-6 h-6" />
            </button>
            <h2 className="text-xl font-bold">
              Modifica {selectedCount === 1 ? 'transazione' : `${selectedCount} transazioni`}
            </h2>
            <div className="w-6"></div>
          </div>
        </div>

        <form onSubmit={handleSubmit} className="p-6 space-y-6">
          <p className="text-sm text-gray-500 dark:text-gray-400">
            Spunta i campi da modificare, poi inserisci il nuovo valore.
          </p>
          <div className="flex items-start gap-3">
            <input
              id="bulk-edit-amount"
              type="checkbox"
              checked={formState.amount.enabled}
              onChange={() => handleToggleField('amount')}
              className="mt-1 h-4 w-4 text-blue-600 focus:ring-blue-500"
            />
            <div className="flex-1">
              <label htmlFor="bulk-edit-amount" className="block text-sm font-semibold text-gray-900 dark:text-gray-100 mb-3">
                Importo
              </label>
              <div className={`form-input-group ${formState.amount.enabled ? '' : 'opacity-60'}`}>
                <Euro className="form-input-icon" />
                <input
                  type="text"
                  inputMode="decimal"
                  value={formatMoneyInput(formState.amount.value)}
                  onChange={handleAmountChange}
                  placeholder="0,00"
                  className="input form-input-with-icon"
                  disabled={!formState.amount.enabled}
                />
              </div>
            </div>
          </div>

          <div className="flex items-start gap-3">
            <input
              id="bulk-edit-category"
              type="checkbox"
              checked={formState.category.enabled}
              onChange={() => handleToggleField('category')}
              className="mt-1 h-4 w-4 text-blue-600 focus:ring-blue-500"
            />
            <div className="flex-1">
              <label htmlFor="bulk-edit-category" className="block text-sm font-semibold text-gray-900 dark:text-gray-100 mb-3 flex items-center gap-2">
                <Tag className="w-5 h-5 text-gray-500 dark:text-gray-400" />
                Categoria
              </label>
              <div className={formState.category.enabled ? '' : 'opacity-60'}>
                <CustomSelect
                  value={formState.category.value}
                  onChange={(value) => setFormState(prev => ({
                    ...prev,
                    category: { ...prev.category, value }
                  }))}
                  options={categoryOptions}
                  placeholder="Seleziona categoria"
                  className="h-12"
                  disabled={!formState.category.enabled}
                />
              </div>
            </div>
          </div>

          <div className="flex items-start gap-3">
            <input
              id="bulk-edit-destination"
              type="checkbox"
              checked={formState.destination.enabled}
              onChange={() => handleToggleField('destination')}
              className="mt-1 h-4 w-4 text-blue-600 focus:ring-blue-500"
            />
            <div className="flex-1 space-y-4">
              <label htmlFor="bulk-edit-destination" className="block text-sm font-semibold text-gray-900 dark:text-gray-100 mb-3 flex items-center gap-2">
                <Wallet className="w-5 h-5 text-gray-500 dark:text-gray-400" />
                Conto / Persona
              </label>
              <div className={`grid grid-cols-2 gap-3 ${destinationDisabled ? 'opacity-60' : ''}`}>
                <button
                  type="button"
                  onClick={() => setFormState(prev => ({
                    ...prev,
                    destination: { ...prev.destination, mode: 'wallet' }
                  }))}
                  className={`h-11 rounded-xl text-sm font-semibold transition-all ${
                    formState.destination.mode === 'wallet'
                      ? 'bg-blue-600 text-white'
                      : 'bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-200'
                  }`}
                  disabled={destinationDisabled}
                >
                  Conto
                </button>
                <button
                  type="button"
                  onClick={() => setFormState(prev => ({
                    ...prev,
                    destination: { ...prev.destination, mode: 'debt' }
                  }))}
                  className={`h-11 rounded-xl text-sm font-semibold transition-all ${
                    formState.destination.mode === 'debt'
                      ? 'bg-emerald-600 text-white'
                      : 'bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-200'
                  }`}
                  disabled={destinationDisabled}
                >
                  Persona
                </button>
              </div>

              {formState.destination.mode === 'wallet' ? (
                <div className={destinationDisabled ? 'opacity-60' : ''}>
                  <CustomSelect
                    value={formState.destination.walletId}
                    onChange={(value) => setFormState(prev => ({
                      ...prev,
                      destination: { ...prev.destination, walletId: value }
                    }))}
                    options={walletOptions}
                    placeholder="Seleziona conto"
                    className="h-12"
                    disabled={destinationDisabled}
                  />
                </div>
              ) : (
                <div className={destinationDisabled ? 'opacity-60' : ''}>
                  <div className="form-input-group relative">
                    <input
                      type="text"
                      value={formState.destination.personName}
                      onChange={(event) => setFormState(prev => ({
                        ...prev,
                        destination: { ...prev.destination, personName: event.target.value }
                      }))}
                      placeholder="Nome persona"
                      className="input form-input-with-icon"
                      autoComplete="off"
                      spellCheck="false"
                      list="bulk-edit-persons"
                      disabled={destinationDisabled}
                    />
                    <User className="form-input-icon" />
                    <datalist id="bulk-edit-persons">
                      {existingPersons.map((person) => (
                        <option key={person} value={person} />
                      ))}
                    </datalist>
                  </div>
                </div>
              )}
            </div>
          </div>

          <div className="flex items-start gap-3">
            <input
              id="bulk-edit-store"
              type="checkbox"
              checked={formState.store.enabled}
              onChange={() => handleToggleField('store')}
              className="mt-1 h-4 w-4 text-blue-600 focus:ring-blue-500"
            />
            <div className="flex-1">
              <label htmlFor="bulk-edit-store" className="block text-sm font-semibold text-gray-900 dark:text-gray-100 mb-3 flex items-center gap-2">
                <Store className="w-5 h-5 text-gray-500 dark:text-gray-400" />
                Negozio
              </label>
              <div className={`form-input-group ${formState.store.enabled ? '' : 'opacity-60'}`}>
                <Store className="form-input-icon" />
                <input
                  type="text"
                  value={formState.store.value}
                  onChange={(event) => setFormState(prev => ({
                    ...prev,
                    store: { ...prev.store, value: event.target.value }
                  }))}
                  placeholder="Nome negozio"
                  className="input form-input-with-icon"
                  disabled={!formState.store.enabled}
                />
              </div>
            </div>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div className="flex items-start gap-3">
              <input
                id="bulk-edit-date"
                type="checkbox"
                checked={formState.date.enabled}
                onChange={() => handleToggleField('date')}
                className="mt-1 h-4 w-4 text-blue-600 focus:ring-blue-500"
              />
              <div className="flex-1">
                <label htmlFor="bulk-edit-date" className="block text-sm font-semibold text-gray-900 dark:text-gray-100 mb-3 flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <Calendar className="w-5 h-5 text-gray-500 dark:text-gray-400" />
                    Data
                  </div>
                  <div className="flex items-center gap-1">
                    <button
                      type="button"
                      onClick={() => handleDateShift(-1)}
                      disabled={!formState.date.enabled}
                      className="p-1 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg transition-colors text-blue-600 dark:text-blue-400 disabled:opacity-50"
                      title="Giorno precedente"
                    >
                      <ChevronLeft className="w-4 h-4" />
                    </button>
                    <button
                      type="button"
                      onClick={() => handleDateShift(1)}
                      disabled={!formState.date.enabled}
                      className="p-1 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg transition-colors text-blue-600 dark:text-blue-400 disabled:opacity-50"
                      title="Giorno successivo"
                    >
                      <ChevronRight className="w-4 h-4" />
                    </button>
                  </div>
                </label>
                <input
                  type="date"
                  value={formState.date.value}
                  onChange={(event) => setFormState(prev => ({
                    ...prev,
                    date: { ...prev.date, value: event.target.value }
                  }))}
                  className={`input ${formState.date.enabled ? '' : 'opacity-60'}`}
                  disabled={!formState.date.enabled}
                />
              </div>
            </div>

            <div className="flex items-start gap-3">
              <input
                id="bulk-edit-time"
                type="checkbox"
                checked={formState.time.enabled}
                onChange={() => handleToggleField('time')}
                className="mt-1 h-4 w-4 text-blue-600 focus:ring-blue-500"
              />
              <div className="flex-1">
                <label htmlFor="bulk-edit-time" className="block text-sm font-semibold text-gray-900 dark:text-gray-100 mb-3 flex items-center gap-2">
                  <Clock className="w-5 h-5 text-gray-500 dark:text-gray-400" />
                  Ora
                </label>
                <input
                  type="time"
                  value={formState.time.value}
                  onChange={(event) => setFormState(prev => ({
                    ...prev,
                    time: { ...prev.time, value: event.target.value }
                  }))}
                  className={`input ${formState.time.enabled ? '' : 'opacity-60'}`}
                  disabled={!formState.time.enabled}
                />
              </div>
            </div>
          </div>

          <div className="flex gap-3">
            <button
              type="button"
              onClick={onClose}
              className="btn btn-secondary flex-1"
            >
              Annulla
            </button>
            <button
              type="submit"
              className="btn btn-primary flex-1"
            >
              Applica
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

export default BulkEditDialog;
